import type { OrderData, OrderEventData, PartialExit } from '../../bot/types/chartData'
import { closesByKey, pairKeyOf, ticketsByKey, tradePips } from '../../bot/utils/orderPairing'

// Re-exported because this module's own consumers and tests have always reached pips
// through it. The definition moved to bot/utils/orderPairing so the replay chart's order
// lines share the one pairing and the one pip maths.
export { tradePips }

type NetChange = Extract<OrderEventData, { kind: 'position_increased' | 'position_reduced' | 'position_modified' }>

/**
 * One row of the walkthrough's trade pane.
 *
 * There is no money anywhere in this shape, deliberately. A walkthrough answers "does the code
 * enter and exit where I drew?", not "how much would this make" — the moment a balance or a
 * dollar P&L appears, users read it as a performance claim, which is what the Backtest tab is
 * for. P/L is in pips only. Volume is lots, not money: it is what a scale-in changes.
 */
export interface WalkthroughTrade {
  /** 1-based, in entry order — the "#1 / #2" the user sees against the chart markers. */
  ticket: number
  /** The `opened` event's id: unique per position, the row key. */
  openOrderId: OrderData['orderId']
  type: 'Buy' | 'Sell'
  entryTs: number
  /** Average entry while open (a scale-in moves it); the position's average once closed. */
  entry: number
  /** Current lots while open (scale-ins and partial exits move it); total entered once closed. */
  volume: number | null
  sl: number | null
  tp: number | null
  exitTs: number | null
  exit: number | null
  /** Signed pips. Realised once closed; floating against the last revealed close while open. */
  pips: number | null
  exitReason?: string
  /** Partial exits the position took before its final close (0 for legacy rows and open ones). */
  exits: number
  partialExits?: PartialExit[]
  /**
   * Open rows, engine API 3: how many times the strategy moved the stops (absent when it never
   * did), with the levels the position OPENED with beside the current `sl` / `tp`.
   */
  modifications?: number
  openSl?: number | null
  openTp?: number | null
}

export interface WalkthroughTrades {
  open: WalkthroughTrade[]
  closed: WalkthroughTrade[]
}

/**
 * Partition the run's order events into what is open and what has closed as of the playhead.
 *
 * Both lists are derived from the full event set rather than from the chart's windowed orders:
 * the chart prunes orders when it pages, and a trade list that silently loses rows as the user
 * pans would be worse than useless for validation.
 *
 * Pairing is by position (`pairKeyOf`): the engine ticket when the rows carry one, else the
 * entry time, as it always was. `activity` (the walkthrough's netting events) folds scale-ins
 * and partial exits into an open row's volume, average entry and stops as the playhead passes
 * them, and the strategy's own stop moves (engine API 3) into its stops; legacy runs have none
 * and read exactly as before.
 *
 * @param orders     every order event for the run, unix seconds
 * @param playheadTs replay cursor, unix seconds
 * @param pipSize    from backtest_config.pip_size
 * @param markPrice  last revealed close, for floating pips on open trades
 * @param activity   the run's order/netting events (optional)
 */
export function buildWalkthroughTrades(
  orders: OrderData[],
  playheadTs: number,
  pipSize: number,
  markPrice: number | null,
  activity: OrderEventData[] = [],
): WalkthroughTrades {
  const opens = orders
    .filter((o) => o.eventType === 'opened')
    .sort((a, b) => a.time - b.time)

  // position key -> the closing event, for every close the playhead has reached.
  const closes = closesByKey(orders, playheadTs)
  // Numbered over the whole set rather than over what is revealed, so a trade keeps its number
  // as the replay runs. Shared with the chart's order tooltip — see ticketsByKey for why the
  // two cannot each count for themselves.
  const tickets = ticketsByKey(orders)

  // Netting changes per position ticket, in time order, up to the playhead.
  const changes = new Map<number, NetChange[]>()
  for (const a of activity) {
    if (a.time > playheadTs) continue
    if (a.kind !== 'position_increased' && a.kind !== 'position_reduced' && a.kind !== 'position_modified') continue
    const list = changes.get(a.ticket)
    if (list) list.push(a)
    else changes.set(a.ticket, [a])
  }
  for (const list of changes.values()) list.sort((a, b) => a.time - b.time || Number(a.eventId) - Number(b.eventId))

  const open: WalkthroughTrade[] = []
  const closed: WalkthroughTrade[] = []

  opens.forEach((o, i) => {
    const key = pairKeyOf(o)
    const ticket = (key == null ? undefined : tickets.get(key)) ?? i + 1
    if (o.time > playheadTs) return

    const type: 'Buy' | 'Sell' = o.type === 'BUY' ? 'Buy' : 'Sell'
    const close = key == null ? undefined : closes.get(key)

    if (close) {
      const entry = close.entryPrice ?? o.price
      const exitPrice = close.avgExitPrice ?? close.exitPrice ?? close.price
      closed.push({
        ticket,
        openOrderId: o.orderId,
        type,
        entryTs: o.time,
        entry,
        volume: close.volume ?? o.volume ?? null,
        // A closed position has no live stop or target left to show.
        sl: null,
        tp: null,
        exitTs: close.time,
        exit: exitPrice,
        pips: tradePips(o.type, entry, exitPrice, pipSize),
        exitReason: close.exitReason,
        // Engine API 3 embeds at most 100 partial exits and reports the true count beside them.
        exits: close.partialExitCount ?? close.partialExits?.length ?? 0,
        ...(close.partialExits && close.partialExits.length > 0 ? { partialExits: close.partialExits } : {}),
      })
      return
    }

    let volume: number | null = o.volume ?? null
    let entry = o.price
    let sl: number | null = o.sl || null
    let tp: number | null = o.tp || null
    let modifications = 0
    if (o.ticket != null) {
      for (const c of changes.get(o.ticket) ?? []) {
        if (c.kind === 'position_increased') {
          volume = c.volume
          entry = c.entryPrice
          sl = c.sl || null
          tp = c.tp || null
        } else if (c.kind === 'position_modified') {
          sl = c.sl || null
          tp = c.tp || null
          modifications += 1
        } else {
          volume = c.remainingVolume
        }
      }
    }

    open.push({
      ticket,
      openOrderId: o.orderId,
      type,
      entryTs: o.time,
      entry,
      volume,
      sl,
      tp,
      exitTs: null,
      exit: null,
      pips: markPrice != null ? tradePips(o.type, entry, markPrice, pipSize) : null,
      exitReason: undefined,
      exits: 0,
      ...(modifications > 0 ? { modifications, openSl: o.sl || null, openTp: o.tp || null } : {}),
    })
  })

  // Newest first in both lists: the trade the user is watching is the one that just happened.
  open.sort((a, b) => b.entryTs - a.entryTs)
  closed.sort((a, b) => (b.exitTs ?? 0) - (a.exitTs ?? 0))

  return { open, closed }
}
